import Carregando from '../components/Carregando';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, CircularProgress, TextField, MenuItem, Button,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Chip, Alert, Tabs, Tab,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import AcessoNegado from '../components/AcessoNegado';
import PageHeader from '../components/PageHeader';
import dayjs from 'dayjs';
import { formatDias } from '../utils/formatters';

const AZUL = '#13375F';
const ESCURO = '#1a1a2e';

const STATUS_PROJETO = {
  planejamento: { label: 'Planejamento', cor: '#64748B', fundo: '#F1F5F9' },
  em_andamento: { label: 'Em andamento', cor: '#13375F', fundo: '#E3ECF6' },
  pausado: { label: 'Pausado', cor: '#BA7517', fundo: '#FAEEDA' },
};

const SITUACAO = {
  ritmo: { label: 'No ritmo', cor: '#639922', fundo: '#EAF3DE' },
  atencao: { label: 'Atenção', cor: '#BA7517', fundo: '#FAEEDA' },
  critico: { label: 'Crítico', cor: '#E24B4A', fundo: '#FCEBEB' },
};

const cabecalhoTabela = { backgroundColor: '#f5f5f5', whiteSpace: 'nowrap', fontWeight: 600 };

function Etiqueta({ cor, fundo, children }) {
  return (
    <Chip
      label={children}
      size="small"
      sx={{ color: cor, backgroundColor: fundo, fontWeight: 600, borderRadius: '6px' }}
    />
  );
}

function Indicador({ titulo, valor, detalhe, cor = ESCURO }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary">{titulo}</Typography>
        <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1.2, color: cor }}>{valor}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ minHeight: 18, display: 'block' }}>
          {detalhe || ' '}
        </Typography>
      </CardContent>
    </Card>
  );
}

function corDoPercentual(pct) {
  if (pct >= 100) return SITUACAO.critico;
  if (pct >= 80) return SITUACAO.atencao;
  return SITUACAO.ritmo;
}

function corDaMeta(pct) {
  if (pct >= 90) return SITUACAO.ritmo;
  if (pct >= 60) return SITUACAO.atencao;
  return SITUACAO.critico;
}

const texto = (v) => (v || '').toString().toLowerCase();

function mesAno(iso) {
  return iso ? dayjs(iso).format('MM/YYYY') : '—';
}

function numero(v) {
  if (v === null || v === undefined) return '—';
  return Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 2 });
}

export default function Relatorios() {
  const { temAcesso } = useAuth();
  const acessoLiberado = temAcesso('relatorios');

  const [aba, setAba] = useState('projetos');
  const [relatorio, setRelatorio] = useState(null);
  const [erroRelatorio, setErroRelatorio] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exportando, setExportando] = useState(false);
  const [erroExportar, setErroExportar] = useState('');

  const [busca, setBusca] = useState('');
  const [statusFiltro, setStatusFiltro] = useState('');
  const [situacaoFiltro, setSituacaoFiltro] = useState('');
  const [buscaIndicador, setBuscaIndicador] = useState('');

  const carregar = useCallback(() => {
    setLoading(true);
    setErroRelatorio(false);
    api.get('/relatorio-projetos/')
      .then((r) => setRelatorio(r.data))
      .catch(() => setErroRelatorio(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!acessoLiberado) { setLoading(false); return; }
    carregar();
  }, [acessoLiberado, carregar]);

  const projetos = useMemo(() => {
    const lista = (relatorio?.projetos || []).filter((p) => {
      if (statusFiltro && p.status !== statusFiltro) return false;
      if (situacaoFiltro && p.risco !== situacaoFiltro) return false;
      if (busca) {
        const alvo = `${p.nome} ${p.cliente_nome} ${p.contrato_nome || ''}`.toLowerCase();
        if (!alvo.includes(busca.toLowerCase())) return false;
      }
      return true;
    });
    // Projetos do mesmo contrato ficam juntos.
    return [...lista].sort((a, b) => (
      texto(a.cliente_nome).localeCompare(texto(b.cliente_nome))
      || texto(a.contrato_nome).localeCompare(texto(b.contrato_nome))
      || texto(a.nome).localeCompare(texto(b.nome))
    ));
  }, [relatorio, busca, statusFiltro, situacaoFiltro]);

  const indicadores = useMemo(() => (
    (relatorio?.indicadores || []).filter((i) => {
      if (!buscaIndicador) return true;
      const alvo = `${i.projeto_nome} ${i.cliente_nome} ${i.indicador} ${i.contrato_nome || ''}`.toLowerCase();
      return alvo.includes(buscaIndicador.toLowerCase());
    })
  ), [relatorio, buscaIndicador]);

  const exportarExcel = async () => {
    setExportando(true);
    setErroExportar('');
    try {
      const resposta = await api.get('/relatorio-projetos/excel/', { responseType: 'blob' });
      const url = URL.createObjectURL(resposta.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio_projetos_${dayjs().format('YYYY-MM-DD')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErroExportar('Não foi possível gerar o Excel agora. Tente de novo; se continuar, avise o Admin.');
    } finally {
      setExportando(false);
    }
  };

  if (!acessoLiberado) {
    return (
      <AcessoNegado mensagem="Você não tem acesso aos Relatórios. Fale com o Admin se precisar desse acesso." />
    );
  }

  const resumo = relatorio?.resumo;
  const noRitmo = resumo ? resumo.projetos_ativos - resumo.em_atencao - resumo.criticos : 0;

  return (
    <Box>
      <PageHeader
        titulo="Relatórios"
        acoes={(
          <Button
            variant="contained"
            startIcon={exportando ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
            onClick={exportarExcel}
            disabled={exportando || loading}
            sx={{ backgroundColor: ESCURO, '&:hover': { backgroundColor: '#e94560' } }}
          >
            {exportando ? 'Gerando Excel...' : 'Exportar Excel'}
          </Button>
        )}
      />

      {erroExportar && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErroExportar('')}>{erroExportar}</Alert>}

      {loading ? (
        <Carregando />
      ) : (
        <>
          {erroRelatorio && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={<Button color="inherit" size="small" onClick={carregar}>Tentar de novo</Button>}
            >
              Não foi possível carregar os projetos.
            </Alert>
          )}

          {resumo && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 6, md: 3 }}>
                <Indicador
                  titulo="Projetos ativos"
                  valor={resumo.projetos_ativos}
                  detalhe={resumo.em_contrato_compartilhado > 0
                    ? `${resumo.em_contrato_compartilhado} em ${resumo.contratos_com_varios_projetos} contrato(s) com vários projetos`
                    : null}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <Indicador titulo="No ritmo" valor={noRitmo} cor={SITUACAO.ritmo.cor} detalhe="abaixo de 80% da carga e dos workshops" />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <Indicador
                  titulo="Atenção ou crítico"
                  valor={resumo.em_atencao + resumo.criticos}
                  cor={resumo.criticos > 0 ? SITUACAO.critico.cor : SITUACAO.atencao.cor}
                  detalhe={`${resumo.em_atencao} em atenção, ${resumo.criticos} crítico(s)`}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <Indicador
                  titulo="Indicadores ativos"
                  valor={resumo.indicadores_ativos}
                  detalhe={resumo.indicadores_sem_medicao > 0 ? `${resumo.indicadores_sem_medicao} sem nenhuma medição` : null}
                />
              </Grid>
            </Grid>
          )}

          <Card>
            <Tabs
              value={aba}
              onChange={(_, v) => setAba(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                px: 1, borderBottom: '1px solid #eef0f3',
                '& .MuiTabs-indicator': { backgroundColor: '#e94560' },
                '& .Mui-selected': { color: `${ESCURO} !important`, fontWeight: 700 },
              }}
            >
              <Tab value="projetos" label={`Projetos (${relatorio?.projetos?.length ?? 0})`} />
              <Tab value="indicadores" label={`Indicadores (${relatorio?.indicadores?.length ?? 0})`} />
            </Tabs>

            <CardContent>
              {/* ---------------------------------------------------------- PROJETOS */}
              {aba === 'projetos' && (
                <>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <TextField
                      size="small" placeholder="Buscar por projeto, cliente ou contrato..."
                      value={busca} onChange={(e) => setBusca(e.target.value)} sx={{ minWidth: 380 }}
                    />
                    <TextField
                      select size="small" label="Status" value={statusFiltro}
                      onChange={(e) => setStatusFiltro(e.target.value)} sx={{ minWidth: 190 }}
                    >
                      <MenuItem value="">Todos os ativos</MenuItem>
                      {Object.entries(STATUS_PROJETO).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                    </TextField>
                    <TextField
                      select size="small" label="Situação" value={situacaoFiltro}
                      onChange={(e) => setSituacaoFiltro(e.target.value)} sx={{ minWidth: 160 }}
                    >
                      <MenuItem value="">Todas</MenuItem>
                      {Object.entries(SITUACAO).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                    </TextField>
                  </Box>

                  {projetos.length === 0 ? (
                    <Alert severity="info">Nenhum projeto ativo encontrado com esses filtros.</Alert>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          {/* Cabeçalho em dois níveis: a carga do CONTRATO (base do %) fica separada da carga usada só NESTE projeto */}
                          <TableRow>
                            <TableCell rowSpan={2} sx={cabecalhoTabela}>Projeto</TableCell>
                            <TableCell rowSpan={2} sx={cabecalhoTabela}>Contrato</TableCell>
                            <TableCell rowSpan={2} sx={cabecalhoTabela}>Status</TableCell>
                            <TableCell colSpan={3} align="center" sx={{ ...cabecalhoTabela, color: AZUL, borderBottom: `2px solid ${AZUL}` }}>
                              Carga do contrato
                            </TableCell>
                            <TableCell align="center" sx={{ ...cabecalhoTabela, color: '#64748b', borderBottom: '2px solid #94a3b8' }}>
                              Carga neste projeto
                            </TableCell>
                            <TableCell rowSpan={2} align="center" sx={cabecalhoTabela}>Workshops</TableCell>
                            <TableCell rowSpan={2} align="center" sx={cabecalhoTabela}>% Workshop</TableCell>
                            <TableCell rowSpan={2} align="center" sx={cabecalhoTabela}>Situação</TableCell>
                            <TableCell rowSpan={2} align="center" sx={cabecalhoTabela}>Indicadores</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell align="center" sx={cabecalhoTabela}>Contratada</TableCell>
                            <TableCell align="center" sx={cabecalhoTabela}>Realizada</TableCell>
                            <TableCell align="center" sx={cabecalhoTabela}>% Carga</TableCell>
                            <TableCell align="center" sx={cabecalhoTabela}>Realizada</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {projetos.map((p) => {
                            const st = STATUS_PROJETO[p.status] || STATUS_PROJETO.planejamento;
                            const sit = SITUACAO[p.risco] || SITUACAO.ritmo;
                            const compartilhado = p.tipo_contrato === 'novo';
                            return (
                              <TableRow
                                key={p.id}
                                hover
                                sx={compartilhado ? { '& td:first-of-type': { borderLeft: `3px solid ${AZUL}` } } : undefined}
                              >
                                <TableCell>
                                  <Typography variant="body2" fontWeight={600}>{p.nome}</Typography>
                                  <Typography variant="caption" color="text.secondary">{p.cliente_nome}</Typography>
                                </TableCell>
                                <TableCell>
                                  {compartilhado ? (
                                    <>
                                      <Typography variant="body2">{p.contrato_nome}</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {p.projetos_no_contrato} projeto(s) dividem a carga
                                      </Typography>
                                    </>
                                  ) : '—'}
                                </TableCell>
                                <TableCell><Etiqueta cor={st.cor} fundo={st.fundo}>{st.label}</Etiqueta></TableCell>
                                <TableCell align="center">{formatDias(p.carga_contratada)}</TableCell>
                                <TableCell align="center">{formatDias(p.carga_realizada_contrato)}</TableCell>
                                <TableCell align="center">
                                  <Etiqueta {...corDoPercentual(p.pct_carga)}>{`${Math.round(p.pct_carga)}%`}</Etiqueta>
                                </TableCell>
                                {/* Só faz sentido separar quando o contrato é dividido entre projetos; nos demais seria o mesmo número */}
                                <TableCell align="center" sx={{ color: compartilhado ? 'inherit' : '#94a3b8' }}>
                                  {compartilhado ? formatDias(p.carga_realizada_projeto) : '—'}
                                </TableCell>
                                <TableCell align="center">{`${p.workshops_realizados} / ${p.workshops_contratados}`}</TableCell>
                                <TableCell align="center">
                                  <Etiqueta {...corDoPercentual(p.pct_workshop)}>{`${Math.round(p.pct_workshop)}%`}</Etiqueta>
                                </TableCell>
                                <TableCell align="center"><Etiqueta cor={sit.cor} fundo={sit.fundo}>{sit.label}</Etiqueta></TableCell>
                                <TableCell align="center">{p.indicadores_ativos}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}

              {/* ---------------------------------------------------------- INDICADORES */}
              {aba === 'indicadores' && (
                <>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <TextField
                      size="small" placeholder="Buscar por projeto, cliente ou indicador..."
                      value={buscaIndicador} onChange={(e) => setBuscaIndicador(e.target.value)} sx={{ minWidth: 380 }}
                    />
                  </Box>
                  {indicadores.length === 0 ? (
                    <Alert severity="info">Nenhum indicador ativo encontrado nos projetos ativos.</Alert>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={cabecalhoTabela}>Projeto</TableCell>
                            <TableCell sx={cabecalhoTabela}>Indicador</TableCell>
                            <TableCell sx={cabecalhoTabela}>Unidade</TableCell>
                            <TableCell align="center" sx={cabecalhoTabela}>Baseline</TableCell>
                            <TableCell align="center" sx={cabecalhoTabela}>Meta</TableCell>
                            <TableCell align="center" sx={cabecalhoTabela}>Última medição</TableCell>
                            <TableCell align="center" sx={cabecalhoTabela}>Mês</TableCell>
                            <TableCell align="center" sx={cabecalhoTabela}>% da meta</TableCell>
                            <TableCell align="center" sx={cabecalhoTabela}>Medições</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {indicadores.map((i, idx) => (
                            <TableRow key={`${i.projeto_id}-${idx}`} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>{i.projeto_nome}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {i.cliente_nome}{i.contrato_nome ? ` · ${i.contrato_nome}` : ''}
                                </Typography>
                              </TableCell>
                              <TableCell>{i.indicador}</TableCell>
                              <TableCell>{i.unidade || '—'}</TableCell>
                              <TableCell align="center">{numero(i.baseline)}</TableCell>
                              <TableCell align="center">{numero(i.meta)}</TableCell>
                              <TableCell align="center">{numero(i.ultima_medicao)}</TableCell>
                              <TableCell align="center">{mesAno(i.mes_referencia)}</TableCell>
                              <TableCell align="center">
                                {i.pct_meta === null || i.pct_meta === undefined
                                  ? '—'
                                  : <Etiqueta {...corDaMeta(i.pct_meta)}>{`${i.pct_meta}%`}</Etiqueta>}
                              </TableCell>
                              <TableCell align="center">{i.medicoes}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
